import { useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { CodeBlock } from '../Markdown/CodeBlock';
import { MemoizedReactMarkdown } from '../Markdown/MemoizedReactMarkdown';
import RecommendedReading from './RecommendedReading';
import Icon from '@/components/redesign/icons/Icons';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { DEFAULT_MODEL } from '@/libs/app/const';
import { getInitials } from '@/libs/helper';
import { t } from 'i18next';
import rehypeMathjax from 'rehype-mathjax';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

const MessageDisplayFile = dynamic(() => import('./MessageDisplayFile'));
const MessageDisplayImage = dynamic(() => import('./MessageDisplayImage'));

const PubMedRefs = dynamic(() =>
  import('../answer/PubMedRefs').then((module) => module.PubMedRefs),
);
const FurtherAction = dynamic(() => import('./FurtherAction'));

export const ChatMessage = ({
  message,
  messageIndex,
  handleSend,
  onEdit,
  onPubMedRef,
}) => {
  const {
    state: { selectedConversation, messageIsStreaming, actionsDisabled },
    handleSaveConversation,
  } = useChat();
  const { currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messageContent, setMessageContent] = useState(message.content);
  const [messagedCopied, setMessageCopied] = useState(false);
  const textareaRef = useRef(null);

  const userInitials = getInitials(currentUser?.name);

  const toggleEditing = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (event) => {
    setMessageContent(event.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleEditMessage = () => {
    if (message.content !== messageContent) {
      if (selectedConversation && onEdit) {
        onEdit({ ...message, content: messageContent });
      }
    }
    setIsEditing(false);
  };

  const handleDeleteMessage = () => {
    if (!selectedConversation) return;

    const messages = [...selectedConversation.messages];
    if (
      messageIndex < messages.length - 1 &&
      messages[messageIndex + 1].role === 'assistant'
    ) {
      messages.splice(messageIndex, 2);
    } else {
      messages.splice(messageIndex, 1);
    }
    const updatedConversation = {
      ...selectedConversation,
      messages,
    };

    handleSaveConversation(updatedConversation);
  };

  const handlePressEnter = (e) => {
    if (e.key === 'Enter' && !isTyping && !e.shiftKey) {
      e.preventDefault();
      handleEditMessage();
    }
  };

  const copyOnClick = () => {
    if (!navigator.clipboard) return;

    navigator.clipboard.writeText(message.content).then(() => {
      setMessageCopied(true);
      setTimeout(() => {
        setMessageCopied(false);
      }, 2000);
    });
  };

  useEffect(() => {
    setMessageContent(message.content);
  }, [message.content]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [isEditing]);

  const renderedFiles = useMemo(() => {
    if (!message.files || message.files.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2">
        {message.files.map((file, index) =>
          file.type.startsWith('image/') ? (
            <MessageDisplayImage key={index} index={index} src={file.url} />
          ) : (
            <MessageDisplayFile key={index} index={index} file={file} />
          ),
        )}
      </div>
    );
  }, [message.files]);

  /* ── User message ── */
  if (message.role === 'user') {
    return (
      <div
        className="group mx-auto max-w-[820px] px-8 pb-2.5 pt-[22px]"
        style={{ overflowWrap: 'anywhere' }}
      >
        <div className="flex gap-3.5">
          {/* User avatar */}
          <div className="grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-full bg-ink-100 text-[11px] font-semibold text-ink-700">
            {userInitials}
          </div>

          <div className="min-w-0 flex-1 pt-[5px]">
            {isEditing ? (
              <div className="flex w-full flex-col">
                <textarea
                  ref={textareaRef}
                  className="w-full resize-none rounded-md border border-border-strong bg-white p-3 text-sm text-ink-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  value={messageContent}
                  onChange={handleInputChange}
                  onKeyDown={handlePressEnter}
                  onCompositionStart={() => setIsTyping(true)}
                  onCompositionEnd={() => setIsTyping(false)}
                />
                <div className="mt-3 flex justify-center gap-3">
                  <button
                    className="btn-redesign btn-redesign-primary btn-redesign-sm"
                    onClick={handleEditMessage}
                    disabled={
                      messageContent.trim().length <= 0 || actionsDisabled
                    }
                  >
                    {t('Save & Submit')}
                  </button>
                  <button
                    className="btn-redesign btn-redesign-secondary btn-redesign-sm"
                    onClick={() => {
                      setMessageContent(message.content);
                      setIsEditing(false);
                    }}
                  >
                    {t('Cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col gap-2">
                {renderedFiles}
                <div className="whitespace-pre-wrap text-sm font-medium leading-[1.55] text-ink-900">
                  {message.content}
                </div>
              </div>
            )}
          </div>

          {/* Edit / delete actions */}
          {!isEditing && (
            <div className="flex flex-shrink-0 items-start gap-1 pt-1">
              <button
                aria-label="Edit message"
                className="invisible rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-600 group-hover:visible focus-visible:visible"
                onClick={toggleEditing}
              >
                <Icon.Edit width={14} height={14} />
              </button>
              <button
                aria-label="Delete message"
                className="invisible rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-600 group-hover:visible focus-visible:visible disabled:opacity-50"
                onClick={handleDeleteMessage}
                disabled={actionsDisabled}
              >
                <Icon.Trash width={14} height={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Assistant message ── */
  return (
    <div
      className="group mx-auto max-w-[820px] px-8 pb-[18px] pt-3.5"
      style={{ overflowWrap: 'anywhere' }}
    >
      <div className="flex gap-3.5">
        {/* Assistant avatar */}
        <div className="grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-lg bg-brand-800 text-white">
          <Icon.Pulse width={16} height={16} />
        </div>

        <div className="min-w-0 flex-1">
          {message.type === 'chat' ? (
            <>
              {message.incomplete && (
                <p role="alert" className="mb-2 text-sm text-red-700">
                  {t('This answer was interrupted and is incomplete. Please retry.')}
                </p>
              )}
              <MemoizedReactMarkdown
                className="msg-body prose text-sm leading-[1.62] text-ink-900"
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeMathjax]}
                skipHtml={false}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    if (children.length) {
                      if (children[0] === '▍') {
                        return (
                          <span className="mt-1 animate-pulse cursor-default">
                            ▍
                          </span>
                        );
                      }
                      children[0] = children[0].replace('`▍`', '▍');
                    }

                    const match = /language-(\w+)/.exec(className || '');

                    return !inline ? (
                      <CodeBlock
                        key={Math.random()}
                        language={(match && match[1]) || ''}
                        value={String(children)
                          .replace(/\n$/, '')
                          .replace(/&#36;/g, '$')
                          .replace(/&quot;/g, '"')
                          .replace(/&#39;/g, "'")
                          .replace(/&lt;/g, '<')
                          .replace(/&gt;/g, '>')
                          .replace(/&amp;/g, '&')}
                        {...props}
                      />
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  table({ children }) {
                    return (
                      <table className="border-collapse border border-border px-3 py-1">
                        {children}
                      </table>
                    );
                  },
                  th({ children }) {
                    return (
                      <th className="break-words border border-border bg-ink-100 px-3 py-1 text-ink-900">
                        {children}
                      </th>
                    );
                  },
                  td({ children }) {
                    return (
                      <td className="break-words border border-border px-3 py-1">
                        {children}
                      </td>
                    );
                  },
                }}
              >
                {`${message.content
                  .split(
                    /\*\*(Recommended Readings|References|Google Searches)\*\*/,
                  )[0]
                  .replace(/\$/g, '&#36;')}${
                  messageIsStreaming &&
                  messageIndex ===
                    (selectedConversation?.messages.length ?? 0) - 1
                    ? '`▍`'
                    : ''
                }`}
              </MemoizedReactMarkdown>

              {message.content.split(
                /\*\*(Recommended Readings|References|Google Searches)\*\*/,
              ).length > 2 && (
                <RecommendedReading
                  title={
                    message.content.split(
                      /\*\*(Recommended Readings|References|Google Searches)\*\*/,
                    )[1]
                  }
                  content={
                    message.content.split(
                      /\*\*(Recommended Readings|References|Google Searches)\*\*/,
                    )[2]
                  }
                />
              )}
            </>
          ) : (
            <PubMedRefs articles={message.articles} />
          )}

          {/* Action row */}
          {message.type === 'chat' && (
            <div className="mt-2.5 flex gap-0.5">
              <button
                onClick={copyOnClick}
                className="inline-flex items-center gap-1 rounded-[5px] px-2 py-[5px] text-[11.5px] text-ink-500 hover:bg-ink-100"
              >
                {messagedCopied ? (
                  <>
                    <Icon.Check width={13} height={13} className="text-teal-700" />
                    {t('Copied')}
                  </>
                ) : (
                  <>
                    <Icon.Copy width={13} height={13} />
                    {t('Copy')}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Follow-ups */}
          {messageIndex + 1 ===
            (selectedConversation?.messages?.length ?? 0) &&
            selectedConversation?.followups?.length > 0 && (
              <div className="mt-[18px] border-t border-dashed border-border pt-3.5">
                <div className="t-eyebrow mb-2 text-[10px]">
                  {t('Suggested follow-ups')}
                </div>
                <div className="grid gap-1.5">
                  {selectedConversation.followups.map((followup, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSend({
                          role: 'user',
                          content: followup,
                          type: 'chat',
                        });
                      }}
                      className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-[9px] text-left font-sans text-[12.5px] text-ink-800 hover:border-brand-500 hover:bg-brand-50"
                    >
                      <Icon.ArrowUR
                        width={12}
                        height={12}
                        className="flex-shrink-0 text-ink-400"
                      />
                      {followup}
                    </button>
                  ))}
                </div>
              </div>
            )}

          {/* Further actions (PubMed, feedback) */}
          {selectedConversation.model === DEFAULT_MODEL &&
            (messageIndex + 1 !==
              (selectedConversation?.messages?.length ?? 0) ||
              !actionsDisabled) && (
              <FurtherAction message={message} onPubMedRef={onPubMedRef} />
            )}
        </div>
      </div>
    </div>
  );
};

ChatMessage.displayName = 'ChatMessage';
